import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { contactMailto } from "@/lib/contact";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/PublicHeader";
import {
  Crown, Lock, Lightning, Compass, ChatsCircle, Stack, CheckCircle,
  ArrowRight, Sparkle, Confetti, Quotes, Storefront,
} from "@phosphor-icons/react";

const PERKS = [
  { icon: Crown, title: "Lifetime Founding Council badge", body: "A permanent mark on your account — you were here first, and it stays with you." },
  { icon: Lock, title: "Locked-in introductory pricing", body: "When paid plans go live, you keep your founding rate. It never goes up on you." },
  { icon: Lightning, title: "Early access to new features", body: "Pricing intelligence, build-from-scratch capture and more — you try them first." },
  { icon: Compass, title: "Direct influence on the roadmap", body: "Suggest and vote on what we build next. Founders steer where this goes." },
  { icon: ChatsCircle, title: "Private founders forum + direct line", body: "A quiet room with the team and other serious sellers — real answers, not a bot." },
  { icon: Stack, title: "Grandfathered bonus volume", body: "Extra cross-lists per month, locked in for life as a thank-you for shaping it." },
];

export default function FoundingSeller() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(null);

  useEffect(() => {
    api.get("/founding/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setName((n) => n || user.name || "");
      setEmail((e) => e || user.email || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.includes("@")) {
      toast.error("Please enter your name and a valid email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/founding/claim", { name: name.trim(), email: email.trim(), note: note.trim() });
      setClaimed(data);
      setStats({ claimed: data.claimed, spots_total: data.spots_total, remaining: data.remaining });
      toast.success(data.already ? "You're already on the founding list — seat saved." : "Welcome to the Founding Council! 🎉");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save your spot — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const total = stats?.spots_total ?? 100;
  const taken = stats?.claimed ?? 0;
  const remaining = stats?.remaining ?? total;
  const pct = Math.min(100, Math.round((taken / total) * 100));

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="founding-page">
      <PublicHeader />

      <section className="max-w-5xl mx-auto px-6 pt-16 pb-10">
        <div className="inline-flex items-center gap-2 mb-4" data-testid="founding-eyebrow">
          <Crown size={22} weight="fill" className="text-[#B47A3C]" />
          <span className="label-caps">Founding Council · limited to {total} spots</span>
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl tracking-tight leading-none font-medium mb-5">
          The <span className="italic text-[#B47A3C]">Founding Vision.</span>
        </h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl">
          We're opening a small founding cohort — not because we need the money, but because we need the
          right sellers shaping this. You bring your inventory and your honest feedback; you get lifetime perks
          and a real say in what we build. <strong>No payment now.</strong>
        </p>

        {/* Scarcity meter */}
        <div className="mt-8 max-w-xl" data-testid="founding-meter">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[15px] font-semibold text-[#0D1B2A]">{taken} of {total} founding spots claimed</span>
            <span className="text-sm text-[#8A867D]">{remaining} left</span>
          </div>
          <div className="h-3 bg-[#EDE9E0] overflow-hidden">
            <div className="h-full bg-[#B47A3C] transition-all" style={{ width: `${Math.max(3, pct)}%` }} data-testid="founding-meter-fill" />
          </div>
        </div>
      </section>

      {/* Founder's note */}
      <section className="max-w-5xl mx-auto px-6 pb-6" data-testid="founder-quote">
        <figure className="relative border-l-4 border-[#B47A3C] bg-white pl-6 sm:pl-9 pr-6 py-7 sm:py-9 shadow-sm">
          <Quotes size={64} weight="fill" className="absolute -top-3 right-4 text-[#F0E4DC] pointer-events-none" aria-hidden="true" />
          <div className="inline-flex items-center gap-2 mb-4 relative">
            <Sparkle size={18} weight="fill" className="text-[#B47A3C]" />
            <span className="label-caps">A note from the founder</span>
          </div>
          <blockquote className="font-serif-display text-2xl sm:text-[2rem] leading-snug tracking-tight text-[#0D1B2A] max-w-3xl relative">
            &ldquo;I don&rsquo;t need everyone to believe in my path before I walk it. I need to keep testing
            it against reality until the people it&rsquo;s meant to help begin to believe because it genuinely
            improves their lives.&rdquo;
          </blockquote>
          <figcaption className="mt-5 text-[15px] font-semibold text-[#525252]">
            — The founder, Vintage Vantage
          </figcaption>

          <div className="mt-7 border-t border-[#EDE9E0] pt-6" data-testid="founder-principles">
            <p className="label-caps mb-4">How we build · our operating baseline</p>
            <ol className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
              {[
                "Observe reality.",
                "Reduce wasted effort.",
                "Preserve agency.",
                "Build ownership.",
                "Test assumptions.",
                "Adapt quickly.",
                "Repeat with better evidence.",
              ].map((p, i) => (
                <li key={p} className="flex items-baseline gap-3" data-testid={`principle-${i}`}>
                  <span className="font-serif-display text-lg font-bold text-[#B47A3C] w-5 shrink-0">{i + 1}</span>
                  <span className="text-[17px] font-medium text-[#0D1B2A] leading-snug">{p}</span>
                </li>
              ))}
            </ol>
          </div>
        </figure>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-10 grid lg:grid-cols-2 gap-4" data-testid="founding-perks">
        {PERKS.map((p) => (
          <div key={p.title} className="border border-[#0D1B2A]/15 bg-white p-6 flex gap-4">
            <div className="shrink-0 h-11 w-11 bg-[#0D1B2A] text-[#FDFBF7] flex items-center justify-center">
              <p.icon size={22} weight="fill" />
            </div>
            <div>
              <p className="font-serif-display text-xl font-bold leading-tight mb-1">{p.title}</p>
              <p className="text-[15px] text-[#525252] leading-relaxed">{p.body}</p>
            </div>
          </div>
        ))}
      </section>

      {/* Help choose what we connect next — bait without promises */}
      <section className="max-w-5xl mx-auto px-6 pb-10" data-testid="next-platforms">
        <div className="border-2 border-[#0D1B2A] bg-[#0D1B2A] text-[#FDFBF7] p-8 sm:p-10">
          <div className="inline-flex items-center gap-2 mb-3">
            <Storefront size={20} weight="fill" className="text-[#B47A3C]" />
            <span className="label-caps" style={{ color: "#E5E5E5" }}>Under exploration</span>
          </div>
          <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight leading-tight font-medium mb-3">
            Help choose what we <span className="italic text-[#B47A3C]">connect next.</span>
          </h2>
          <p className="text-[16px] text-[#D8D3C8] leading-relaxed max-w-2xl mb-6">
            We cross-list to Chairish, 1stDibs, AptDeco &amp; Pamono today. These are the candidates we're
            exploring — no dates, no promises. Founding Council members vote on priority.
          </p>
          <div className="flex flex-wrap gap-2.5 mb-2" data-testid="next-platforms-chips">
            {["eBay", "Etsy", "EBTH", "Direct-to-consumer"].map((p) => (
              <span key={p} className="inline-flex items-center gap-1.5 h-9 px-4 border border-[#FDFBF7]/30 bg-white/[0.05] text-[15px] font-semibold">
                {p}
              </span>
            ))}
          </div>
          <p className="text-sm text-[#8A867D] mt-4">
            Claim a seat below and the list is yours to steer.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 pb-8">
        <button onClick={() => navigate("/roadmap")} data-testid="founding-roadmap-link" className="text-[15px] font-semibold text-[#B47A3C] hover:text-[#0D1B2A] inline-flex items-center gap-1.5 transition-colors">
          <Compass size={17} weight="fill" /> See the live feature roadmap — suggest &amp; vote
        </button>
        <p className="text-[15px] text-[#525252] mt-3">
          Prefer to just talk? <a href={contactMailto("Founding Council — question")} data-testid="founding-contact-link" className="font-semibold text-[#B47A3C] hover:text-[#0D1B2A] underline underline-offset-4 transition-colors">Email us directly</a> — a real person reads every note.
        </p>
      </div>

      {/* Claim / success */}
      <section className="max-w-5xl mx-auto px-6 pb-24" data-testid="founding-claim-section">
        {claimed ? (
          <div className="bg-[#0D1B2A] text-[#FDFBF7] p-8 sm:p-12 text-center" data-testid="founding-success">
            <Confetti size={44} weight="fill" className="text-[#B47A3C] mx-auto mb-4" />
            <p className="label-caps mb-2" style={{ color: "#E5E5E5" }}>You're in</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-bold mb-3">
              Founding Council {claimed.spot_number ? `#${claimed.spot_number}` : ""}
            </h2>
            <p className="text-[17px] text-[#E5E5E5] max-w-xl mx-auto leading-relaxed mb-8">
              {claimed.already
                ? "You were already on the list — your founding spot is safe. We'll reach out as founding features open up."
                : "Your spot is locked. We'll email you as founding features and pricing go live — and we'll want your honest take on all of it."}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate(user ? "/dashboard" : "/register")}
                data-testid="founding-next-cta"
                className="h-14 px-8 bg-[#FDFBF7] text-[#0D1B2A] rounded-none font-medium inline-flex items-center gap-2 hover:bg-white transition-transform active:scale-[0.98]"
              >
                {user ? "Back to my dashboard" : "Create my account"} <ArrowRight size={18} weight="bold" />
              </button>
              <button onClick={() => navigate("/guide")} data-testid="founding-guide-cta" className="h-14 px-8 border border-[#FDFBF7] rounded-none font-medium hover:bg-white/10 transition-colors">
                Explore the marketplace guide
              </button>
            </div>
          </div>
        ) : (
          <div className="border-2 border-[#0D1B2A] bg-white p-8 sm:p-10" data-testid="founding-form-card">
            <div className="inline-flex items-center gap-2 mb-2"><Sparkle size={20} weight="fill" className="text-[#B47A3C]" /> <span className="label-caps">Claim your spot</span></div>
            <h2 className="font-serif-display text-3xl sm:text-4xl font-bold tracking-tight mb-2">Reserve your Founding Council seat</h2>
            <p className="text-[15px] text-[#525252] max-w-2xl mb-6">
              Takes ten seconds. No card, no commitment — just tell us who you are and what you sell so we build
              this around real dealers.
            </p>
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label-caps block mb-1.5">Your name</label>
                <input data-testid="founding-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Dealer" className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none" />
              </div>
              <div>
                <label className="label-caps block mb-1.5">Email</label>
                <input data-testid="founding-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-caps block mb-1.5">What do you sell? What's your biggest headache? <span className="text-[#8A867D] normal-case font-normal">(optional)</span></label>
                <textarea data-testid="founding-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="e.g. mid-century + French antiques on Chairish & 1stDibs — re-typing every listing kills me." className="w-full border border-[#0D1B2A]/30 rounded-none px-3 py-2.5 text-[15px] focus:border-[#0D1B2A] outline-none resize-none" />
              </div>
              <div className="sm:col-span-2 flex flex-col sm:flex-row items-center gap-4 mt-1">
                <button data-testid="founding-submit" type="submit" disabled={busy} className="w-full sm:w-auto h-14 px-8 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium text-[17px] inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.98] disabled:opacity-60">
                  {busy ? "Saving your seat…" : "Claim my Founding Council seat"} <Crown size={18} weight="fill" />
                </button>
                <p className="text-sm text-[#8A867D] flex items-center gap-1.5">
                  <CheckCircle size={16} weight="fill" className="text-[#047857]" /> Feedback-first · no payment until you choose to.
                </p>
              </div>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
